import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../config/axios";

/**
 * Departamentos fuera de servicio: rango [desde, disponible_nuevamente) en el
 * que un departamento no se ofrece ni se reserva. La noche de
 * `disponible_nuevamente` ya se puede vender (misma convención que salida).
 */
export interface DepartmentUnavailability {
  id: number;
  departamento: string;
  desde: string;
  disponible_nuevamente: string;
  motivo: string;
  creado_por: string;
  created_at: string;
  updated_at: string;
}

export type DepartmentUnavailabilityInput = Pick<
  DepartmentUnavailability,
  "departamento" | "desde" | "disponible_nuevamente" | "motivo"
>;

/** Prefijo de todas las consultas de fuera de servicio: invalidarlo alcanza a calendario y formularios. */
export const DEPARTMENT_UNAVAILABILITY_KEY = ["department-unavailability"] as const;

export const fetchDepartmentUnavailability = async (desde: string, hasta: string) => {
  const response = await api.get<{ bloqueos: DepartmentUnavailability[] }>(
    "/departamentos/bloqueos",
    { params: { desde, hasta } }
  );
  return response.data.bloqueos;
};

export const useDepartmentUnavailability = (desde: string, hasta: string) =>
  useQuery({
    queryKey: [...DEPARTMENT_UNAVAILABILITY_KEY, desde, hasta],
    queryFn: () => fetchDepartmentUnavailability(desde, hasta),
    enabled: Boolean(desde && hasta && hasta > desde),
    staleTime: 60_000,
  });

const toInput = (input: DepartmentUnavailabilityInput): DepartmentUnavailabilityInput => ({
  departamento: input.departamento,
  desde: input.desde,
  disponible_nuevamente: input.disponible_nuevamente,
  motivo: input.motivo,
});

/** Crea (sin id) o edita (con id). Al terminar invalida todas las consultas de fuera de servicio. */
export const useSaveDepartmentUnavailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ input, id }: { input: DepartmentUnavailabilityInput; id?: number }) => {
      const body = toInput(input);
      const response = id
        ? await api.put<DepartmentUnavailability>(`/departamentos/bloqueos/${id}`, body)
        : await api.post<DepartmentUnavailability>("/departamentos/bloqueos", body);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DEPARTMENT_UNAVAILABILITY_KEY }),
  });
};

export const useDeleteDepartmentUnavailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/departamentos/bloqueos/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DEPARTMENT_UNAVAILABILITY_KEY }),
  });
};
